import React, { useEffect } from 'react';
import { z } from 'zod';
import { SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { useUiComponentsInternationalContext } from '@repo/static-translations';
import { Button } from '@repo/ui-components/shadcn/Button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@repo/ui-components/shadcn/Form';
import {
  RadioGroup,
  RadioGroupItem,
} from '@repo/ui-components/shadcn/RadioGroup';
import { DealerProps } from './BlockBuilderProspectScheduler.types';
import { dealerSelectFormSchema } from './formSchema';

interface DealerSelectFormProps {
  dealers: DealerProps[];
  selectedDealer: DealerProps | null;
  handleDealerSelect: (dealer: DealerProps) => void;
  handleDealerSubmit: () => Promise<void>;
  loading: boolean;
  dealerSubmitButtonLabel?: string | null;
}

const DealerSelect: React.FC<DealerSelectFormProps> = ({
  dealers,
  handleDealerSelect,
  handleDealerSubmit,
  loading,
  dealerSubmitButtonLabel,
}) => {
  const { tt } = useUiComponentsInternationalContext();

  const schemaDealerSelect = dealerSelectFormSchema(
    tt(
      s =>
        s.blockBuilderProspectSchedulerComponent
          .dealerSelectionRequiredValidationMessage
    )
  );

  type FormInputs = z.infer<typeof schemaDealerSelect>;

  const form = useForm<FormInputs>({
    resolver: zodResolver(
      dealerSelectFormSchema(
        tt(
          s =>
            s.blockBuilderProspectSchedulerComponent
              .dealerSelectionRequiredValidationMessage
        )
      )
    ),
    mode: 'all',
    defaultValues: {
      dealer: dealers.length > 0 ? dealers[0].name : '',
    },
  });

  const { handleSubmit } = form;

  const onSubmit: SubmitHandler<FormInputs> = async (_, event) => {
    event.preventDefault();
    await handleDealerSubmit();
  };

  // Ensure handleDealerSelect is called for the first dealer when the component mounts
  useEffect(() => {
    if (dealers.length > 0) {
      handleDealerSelect(dealers[0]); // Trigger handleDealerSelect with the first dealer
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run once on mount

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <fieldset>
          <legend>
            <h2 className="mb-4">
              {tt(
                s => s.blockBuilderProspectSchedulerComponent.selectDealerLabel
              )}
            </h2>
          </legend>
          <FormField
            control={form.control}
            name="dealer"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <RadioGroup
                    className="grid gap-2"
                    onValueChange={value => {
                      field.onChange(value);
                      const selectedDealer = dealers.find(
                        dealer => dealer.name === value
                      );
                      if (selectedDealer) {
                        handleDealerSelect(selectedDealer); // Trigger handleDealerSelect on change
                      }
                    }}
                    defaultValue={field.value}
                  >
                    {dealers.map(dealer => {
                      const dealerId = dealer.name.replaceAll(' ', '');
                      return (
                        <div key={dealer.id} className="mb-2">
                          <FormItem className="flex items-center space-x-3 space-y-0">
                            <FormControl>
                              <RadioGroupItem
                                aria-describedby={`${dealerId}-description`}
                                value={dealer.name}
                              />
                            </FormControl>
                            <FormLabel className="cursor-pointer">
                              {dealer.name}
                            </FormLabel>
                          </FormItem>
                          <FormDescription
                            id={`${dealerId}-description`}
                            className="m-0 ml-7"
                          >
                            {`${tt(
                              s =>
                                s.blockBuilderProspectSchedulerComponent
                                  .dealerSelectServiceAreasLabel
                            )} ${dealer.mainServiceArea}`}
                          </FormDescription>
                        </div>
                      );
                    })}
                  </RadioGroup>
                </FormControl>
                <div className="mb-4">
                  <FormMessage />
                </div>
              </FormItem>
            )}
          />

          <Button variant="primary" type="submit" disabled={loading}>
            {loading
              ? tt(
                  s =>
                    s.blockBuilderProspectSchedulerComponent.buttonLoadingText
                )
              : dealerSubmitButtonLabel ??
                tt(
                  s =>
                    s.blockBuilderProspectSchedulerComponent
                      .buttonDealerSubmitText
                )}
          </Button>
        </fieldset>
      </form>
    </Form>
  );
};

export default DealerSelect;
